import { useState } from 'react';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import intl from 'components/i18n/ReactIntlWrapper';
import { handleAPIError } from 'common/errorHandler';
import {
  Box,
  Typography,
  Button,
  Chip,
  Card,
} from '@mui/material';
import { User } from 'axiosApi/models';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { DEFAULT_PAGINATION, Sort, useSearch } from 'hooks/useSearch';
import { DataGrid, GridColDef, GridInitialState } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import Spinner from 'components/Layout/Spinner';
import DefaultActions from 'components/DataGrid/DefaultActions';
import { SCOPES } from 'common/permissions';
import { getUserEntity } from 'common/UserEntityProvider';
import SearchInput from 'components/SearchInput';
import Title from 'components/common/Title';
import { useNavigate } from 'react-router';

const DEFAULT_SORT: Sort = {
  sort: 'email',
  order: 'asc',
};

const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const { employeeApi }: Api = useApi();
  const toast = useToastMessageQueue();
  const entityType = getUserEntity();

  const [errors, setErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState<string>('');

  const INITIAL_STATE: GridInitialState = {
    pagination: {
      paginationModel: {
        page: DEFAULT_PAGINATION.pageNum,
        pageSize: DEFAULT_PAGINATION.pageSize,
      },
    },
    sorting: {
      sortModel: [
        {
          field: DEFAULT_SORT.sort,
          sort: DEFAULT_SORT.order,
        },
      ],
    },
  };

  const columns: GridColDef[] = [
    {
      field: 'email',
      headerName: intl.get('userList.table.thead.email'),
      width: 250,
      flex: 1,
      cellClassName: (params) => {
        return params.row.active ? '' : 'user-inactive';
      },
    },
    {
      field: 'active',
      headerName: '',
      width: 100,
      type: 'boolean',
      renderCell: (params) => (
        <Box display="flex" justifyContent="center">
          {!params.value && (
            <Chip
              label={intl.get('statusSelect.inactive') || 'Inactive'}
              color="error"
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 150,
      renderCell: (params) => (
        <DefaultActions
          row={params.row}
          entityType={entityType.entityType}
          onEdit={() => navigate(`/user/detail/${params.row.id}`)}
          editScopes={[SCOPES['users.edit']]}
        />
      ),
    },
  ];

  const processError = (error: any) => {
    handleAPIError(error, toast, errors);
    setErrors({ ...errors });
  };

  const apiPromise = (
    searchTerm: string,
    pageNum: number,
    pageSize: number,
    sort: string,
    order: string
  ) => {
    return employeeApi.apiVversionUserSearchGet('1', searchTerm, pageNum, pageSize, sort, order);
  };

  const { data, loading, onPaginationModelChange, handleSortModelChange } = useSearch<User>({
    apiPromise: apiPromise,
    searchTerm: searchTerm,
    defaultSort: DEFAULT_SORT,
    onError: processError,
  });

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  return (
    <Card sx={{ m: 2 }}>
    <Box sx={{ p: 3 }}>
      <Title>{intl.get('users.header')}</Title>
      <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
        {intl.get('users.header')}
      </Typography>
      <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} mb={2}>
        <SearchInput value={searchTerm} onChange={setSearchTerm} onClear={handleClearSearch} />
        <Button onClick={() => navigate(`/user/new`)}>
          <AddIcon /> {intl.get('user.new') ? intl.get('user.new') : intl.get('button.new')}
        </Button>
      </Box>
      <Box style={{ width: '100%', display: 'flex' }}>
        {loading && !data && <Spinner />}
        {data && (
          <DataGrid
            rows={data.queryResult || []}
            columns={columns}
            rowCount={data.totalRecords || 0}
            loading={loading}
            initialState={INITIAL_STATE}
            onPaginationModelChange={onPaginationModelChange}
            onSortModelChange={handleSortModelChange}
          />
        )}
      </Box>
    </Box>
    </Card>
  );
};

export default UsersPage;